
import { defineComponent, onMounted, ref } from "vue";
import { useStore } from "vuex";

import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import ApiService from "@/core/services/ApiService";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { DrawerComponent } from "@/assets/ts/components/_DrawerComponent";
import UpdateQueueDrawer from "@/layout/extras/UpdateQueueDrawer.vue";

export default defineComponent({
  name: "merchants-queue",
  components: {
    Datatable,
    UpdateQueueDrawer,
  },
  setup() {
    const store = useStore();
    const selectedMerchant = ref({});

    const openDrawer = (merchant) => {
      selectedMerchant.value = merchant;
      DrawerComponent?.getInstance("update_queue_drawer")?.show();
    };

    const didClosedDrawer = async () => {
      loading.value = true;
      tableData.value = await getQueues(pagination.value, sort.value);
      loading.value = false;
    };

    onMounted(async () => {
      setCurrentPageTitle("Merchants Queue");

      loading.value = true;
      setTimeout(async () => {
        tableData.value = await getQueues(pagination.value, sort.value);
        DrawerComponent.reinitialization();
        loading.value = false;
      }, 500);
    });

    const getQueues = async (pagination, sort) => {
      try {
        const { page, rowsPerPage } = pagination;
        const offset = (page - 1) * rowsPerPage;

        let sortParam = "";
        const { columnName, order } = sort;
        if (columnName !== "") {
          sortParam = `&sort=${columnName},${order}`;
        }
        const { data } = await ApiService.get(
          `merchants/queue?group_id=${store.getters.currentUser.data.companygroupid}&offset=${offset}&limit=${rowsPerPage}${sortParam}`
        );
        return data;
      } catch (err) {
        console.log(err);
        return {
          data: [],
          meta: {
            total: 0,
          },
        };
      }
    };

    const pagination = ref({
      page: 1,
      rowsPerPage: 5,
    });
    const sort = ref({
      columnName: "merchant_id",
      order: "desc",
    });
    const checkedmerchants = ref([]);
    const tableHeader = ref([
      {
        key: "checkbox",
        sortable: false,
      },
      {
        name: "Merchant ID",
        key: "merchant_id",
        sortable: true,
      },
      {
        name: "Mid Number",
        key: "mid_number",
        sortable: true,
      },
      {
        name: "Customer ID",
        key: "customer_id",
        sortable: true,
      },
      {
        name: "Corp Name",
        key: "corp_name",
        sortable: true,
      },
      {
        name: "Dba Name",
        key: "dba_name",
        sortable: true,
      },
      {
        name: "Bank Status",
        key: "bank_status",
        sortable: true,
      },
      {
        name: "Actions",
        key: "actions",
      },
    ]);

    const tableData = ref({
      data: [],
      meta: {
        total: 0,
      },
    });
    const loading = ref(true);

    const onCurrentChange = async (val) => {
      pagination.value.page = val;

      loading.value = true;
      tableData.value = await getQueues(pagination.value, sort.value);
      loading.value = false;
    };

    const onSort = async (val) => {
      sort.value = val;

      loading.value = true;
      tableData.value = await getQueues(pagination.value, sort.value);
      loading.value = false;
    };

    const onItemsPerPageChange = async (val) => {
      pagination.value.rowsPerPage = val;

      loading.value = true;
      tableData.value = await getQueues(pagination.value, sort.value);
      loading.value = false;
    };

    const bulkActionInsert = async () => {
      await insertMerchants(checkedmerchants.value);
      checkedmerchants.value.splice(0);
    };

    const bulkActionDelete = async () => {
      await deleteMerchants(checkedmerchants.value);
      checkedmerchants.value.splice(0);
    };

    const insertMerchants = async (merchants) => {
      // eslint-disable-next-line
      const payload: any = [];
      // eslint-disable-next-line
      const merchantIds: any = [];

      loading.value = true;
      merchants.forEach((merchant) => {
        const merchantDetails = JSON.parse(merchant.merchantDetails);
        const merchantProduct = JSON.parse(merchant.merchantProduct);
        const checkoutPage = JSON.parse(merchant.checkoutPage);

        // eslint-disable-next-line
        const data: any = {
          merchant_type: merchant.merchant_type,
          mid_number: merchant.mid_number,
          customer_id: store.getters.currentUser.data.customer_id,
          corp_name: merchant.corp_name,
          dba_name: merchant.dba_name,
          group_id: store.getters.currentUser.data.companygroupid,
          merchantDetails: {
            mid_descriptor: merchantDetails.mid_descriptor,
            crm_uuid: merchantDetails.crm_uuid,
            gw_uuid: merchantDetails.gw_uuid,
            mcc: merchantDetails.mcc,
            logo_url: merchantDetails.logo_url,
          },
          merchantProduct: {
            product_name: merchantProduct.product_name,
            product_description: merchantProduct.product_description,
            product_url: merchantProduct.product_url,
            product_price: merchantProduct.product_price,
            fulfill_type: merchantProduct.fulfill_type,
          },
          checkoutPage: {
            url: checkoutPage.url ? checkoutPage.url : "",
            url_phone: checkoutPage.url_phone,
            email: checkoutPage.email,
            terms_url: checkoutPage.terms_url,
            cancel_url: checkoutPage.cancel_url,
            contact_url: checkoutPage.contact_url,
            refund_url: checkoutPage.refund_url,
          },
          products: [
            "API Sync",
            "API Search",
            "Form Entry",
            "Auto Builder",
            "Auto Match",
            "Auto Update",
            "Update Crm",
          ],
        };

        payload.push(data);
        merchantIds.push(merchant.merchant_id);
      });

      try {
        const res = await ApiService.post(
          "merchants/accounts?action=create_merchant",
          payload
        );
        if (res.data.success) {
          Swal.fire({
            text: "You have successfully inserted the merchant!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          }).then(async function () {
            await deleteMerchantsWithId(merchantIds);
            tableData.value = await getQueues(pagination.value, sort.value);
            loading.value = false;
          });
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
          loading.value = false;
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
        loading.value = false;
      }
    };

    const deleteMerchants = async (merchants) => {
      // eslint-disable-next-line
      const merchantIds: any = [];

      loading.value = true;
      merchants.forEach((merchant) => {
        merchantIds.push(merchant.merchant_id);
      });
      try {
        const res = await ApiService.delete(
          `merchants/queue?merchant_id=${merchantIds.join(",")}`
        );
        if (res.data.success) {
          Swal.fire({
            text: "You have successfully deleted the merchant!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          }).then(async function () {
            tableData.value = await getQueues(pagination.value, sort.value);
            loading.value = false;
          });
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
          loading.value = false;
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
        loading.value = false;
      }
    };

    const deleteMerchantsWithId = async (ids) => {
      try {
        const res = await ApiService.delete(
          `merchants/queue?merchant_id=${ids.join(",")}`
        );
        if (res.data.success) {
          return true;
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });

          return false;
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });

        return false;
      }
    };

    return {
      openDrawer,
      didClosedDrawer,

      selectedMerchant,
      tableData,
      tableHeader,
      loading,
      checkedmerchants,
      pagination,

      onCurrentChange,
      onSort,
      onItemsPerPageChange,
      insertMerchants,
      deleteMerchants,
      bulkActionInsert,
      bulkActionDelete,
    };
  },
});
